import React, { useEffect, useRef, useState } from 'react'

import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { viewAllCards } from '../../api/AdminRequest'
import { Button, Col, Input, Row, Space, Table, Typography, Select, Card as AntCard, Tag } from 'antd';
import { SearchOutlined, FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import { Link } from 'react-router-dom';
import Moment from 'moment'
import { useNavigate } from 'react-router-dom';

const { Title } = Typography;
const { Option } = Select;

function AllCards() {
    const navigate = useNavigate();

    const [allCards, setAllCards] = useState([])
    const [loader, setLoader] = useState(true)
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef(null);
    const [search, setSearch] = useState(false)

    // Filter states
    const [filters, setFilters] = useState({
        expiryFilter: 'all',
        userType: 'all',
        cardType: '',
        cardId: '',
        page: 1,
        limit: 10
    });

    // Local search input (not applied to filters yet)
    const [searchCardId, setSearchCardId] = useState('');

    // Pagination state
    const [pagination, setPagination] = useState({
        totalCards: 0,
        totalPages: 0,
        currentPage: 1,
        limit: 10,
        hasMore: false
    });

    // Fetch cards with current filters
    const fetchCards = async (currentFilters = filters) => {
        try {
            setLoader(true);
            const params = {
                page: currentFilters.page,
                limit: currentFilters.limit,
            };

            if (currentFilters.expiryFilter && currentFilters.expiryFilter !== 'all') {
                params.expiryFilter = currentFilters.expiryFilter;
            }

            // Send userType filter to backend
            if (currentFilters?.userType && currentFilters?.userType !== 'all') {
                params.userType = currentFilters.userType;
            }

            if (currentFilters.cardType) {
                params.cardType = currentFilters.cardType;
            }

            if (currentFilters.cardId) {
                params.cardId = currentFilters.cardId;
            }
            const { data } = await viewAllCards(params);
            setAllCards(data?.cards || []);
            setPagination(data?.pagination || {});
            setLoader(false);
        } catch (error) {
            const errorMessage = error?.response?.data?.message || error.message || 'An error occurred while fetching cards';
            toast.error(errorMessage, {
                position: "top-right",
                autoClose: 3000,
                theme: "dark",
            });
            console.log(error.message);
            setLoader(false);
        }
    };



    useEffect(() => {
        fetchCards(filters);
        setSearch(false)
    }, [filters.page, filters.limit, filters.expiryFilter, filters.cardType, filters.userType, search])

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div
                style={{
                    padding: 8,
                }}
                onKeyDown={(e) => e.stopPropagation()}
            >
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{
                        marginBottom: 8,
                        display: 'block',
                    }}
                />
                <Space>
                    <Button
                        className='bg-blue-800'
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{
                            width: 90,
                        }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{
                            width: 90,
                        }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            clearFilters && handleReset(clearFilters)
                            handleSearch(selectedKeys, confirm)
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined
                style={{
                    color: filtered ? '#1890ff' : undefined,
                }}
            />
        ),
        onFilter: (value, record) =>
            record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{
                        backgroundColor: '#ffc069',
                        padding: 0,
                    }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    // Handle filter changes
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            page: 1 // Reset to page 1 when filter changes
        }));
    };

    // Handle reset filters
    const handleResetFilters = () => {
        setFilters({
            expiryFilter: 'all',
            userType: 'all',
            cardType: '',
            cardId: '',
            page: 1,
            limit: 10
        });
        setSearchCardId(''); // Also reset the local search input
    };

    // Handle card ID search (only applies when search button is clicked or Enter is pressed)
    const handleSearchCardId = (value) => {
        console.log("search", value)
        setFilters(prev => ({
            ...prev,
            cardId: value,
            page: 1 // Reset to page 1 when searching
        }));

        setSearch(true)
    };

    const handleEditCard = (record) => {
        navigate(`/edit-card`, { state: { cardData: record } });
    };

    const columns = [
        {
            title: 'No.',
            dataIndex: '_id',
            key: 'index',
            width: '5%',
            onHeaderCell: () => ({
                style: { whiteSpace: 'nowrap' }
            }),
            render: (text, record, index) => ((pagination.currentPage - 1) * pagination.limit + index + 1),
        },
        {
            title: 'Card ID',
            dataIndex: '_id',
            key: '_id',
            width: '15%',
            onHeaderCell: () => ({
                style: { whiteSpace: 'nowrap' }
            }),
            ...getColumnSearchProps('_id'),
        },
        {
            title: 'Owner Name',
            dataIndex: 'name',
            key: 'name',
            width: '15%',
            onHeaderCell: () => ({
                style: { whiteSpace: 'nowrap' }
            }),
            ...getColumnSearchProps('name'),
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            width: '15%',
            onHeaderCell: () => ({
                style: { whiteSpace: 'nowrap' }
            }),
            ...getColumnSearchProps('email'),
        },
        {
            title: 'User Type',
            dataIndex: 'userID',
            key: 'userType',
            width: '10%',
            onHeaderCell: () => ({
                style: { whiteSpace: 'nowrap' }
            }),
            render: (userID) => {
                const userType = userID?.userType;
                return (
                    <Tag color={userType === 'admin-user' ? 'blue' : 'default'}>
                        {userType || 'N/A'}
                    </Tag>
                )
            }
        },
        {
            title: 'Expiry Status',
            key: 'expiryStatus',
            width: '12%',
            onHeaderCell: () => ({
                style: { whiteSpace: 'nowrap' }
            }),
            render: (_, record) => {
                const isExpired = record?.isExpired;
                const expiryMessage = record?.expiryMessage || 'Active';

                return (
                    <Tag color={isExpired ? 'red' : 'green'}>
                        {expiryMessage}
                    </Tag>
                );
            }
        },
        {
            title: 'Expiry Date',
            key: 'expiryDate',
            width: '12%',
            onHeaderCell: () => ({
                style: { whiteSpace: 'nowrap' }
            }),
            render: (_, record) => {
                // Use backend-provided expiry info
                const expiryInfo = record?.expiryInfo;

                if (!expiryInfo || !expiryInfo.createdDate || !expiryInfo.expireDays) {
                    return <span className="text-gray-400">N/A</span>;
                }

                // Calculate expiry date from backend data
                const createdDate = new Date(expiryInfo.createdDate);
                const expiryDate = new Date(createdDate.getTime() + expiryInfo.expireDays * 24 * 60 * 60 * 1000);
                const isExpired = record?.isExpired || false;

                return (
                    <span className={isExpired ? 'text-red-500 font-bold' : ''}>
                        {Moment(expiryDate).format("Do MMM YYYY")}
                    </span>
                );
            }
        },

        {
            title: 'Actions',
            dataIndex: '_id',
            key: 'actions',
            width: '15%',
            onHeaderCell: () => ({
                style: { whiteSpace: 'nowrap' }
            }),
            render: (_id, record) => {
                return (
                    <Space size="small">
                        <Link to={record?.userID?.adminID?.domain ? record?.userID?.adminID?.domain + `/profile-view/` + _id : process.env.REACT_APP_API_QR_CODE_BASE_URL + _id} target="_blank">
                            <Button className='bg-blue-500 text-white hover:bg-blue-600' size="small">
                                Preview
                            </Button>
                        </Link>

                        <Button
                            onClick={() => handleEditCard(record)}
                            className='bg-blue-500 text-white hover:bg-blue-600' size="small">
                            Edit
                        </Button>

                    </Space>
                )
            }
        },
    ];

    return (
        <>
            <Row gutter={16}>
                <Col span={24}>
                    <Title level={2}>All Cards</Title>
                </Col>
            </Row>

            {/* Filter Section */}
            <AntCard className="mb-4 overflow-x-auto">
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={6}>
                        <div>
                            <label className="block text-sm font-medium mb-1">Search Card ID</label>
                            <Input.Search
                                placeholder="Enter Card ID"
                                style={{ width: '100%' }}
                                value={searchCardId}
                                onChange={(e) => setSearchCardId(e.target.value)}
                                onSearch={handleSearchCardId}
                                allowClear
                                onPressEnter={(e) => handleSearchCardId(e.target.value)}
                            />
                        </div>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <div>
                            <label className="block text-sm font-medium mb-1">Expiry Status</label>
                            <Select
                                style={{ width: '100%' }}
                                value={filters.expiryFilter}
                                onChange={(value) => handleFilterChange('expiryFilter', value)}
                            >
                                <Option value="all">All Cards</Option>
                                <Option value="active">Active Cards</Option>
                                <Option value="expired">Expired Cards</Option>
                            </Select>
                        </div>
                    </Col>

                    <Col xs={24} sm={12} md={6}>
                        <div>
                            <label className="block text-sm font-medium mb-1">User Type</label>
                            <Select
                                style={{ width: '100%' }}
                                value={filters.userType}
                                onChange={(value) => handleFilterChange('userType', value)}
                            >
                                <Option value="all">All Users</Option>
                                <Option value="admin-user">Admin Users</Option>
                                <Option value="normal-user">Normal Users</Option>
                            </Select>
                        </div>
                    </Col>

                    <Col xs={24} sm={12} md={6}>
                        <div>
                            <label className="block text-sm font-medium mb-1">Card Type</label>
                            <Select
                                style={{ width: '100%' }}
                                value={filters.cardType}
                                onChange={(value) => handleFilterChange('cardType', value)}
                                allowClear
                                placeholder="Select card type"
                            >
                                <Option value="ReviewQR">Review Cards</Option>
                                <Option value="ContactCard">Contact Cards</Option>
                                <Option value="">Business Cards</Option>
                                <Option value="TableTaker">Table Taker</Option>
                            </Select>
                        </div>
                    </Col>

                    <Col xs={24} sm={12} md={6}>
                        <div>
                            <label className="block text-sm font-medium mb-1">Actions</label>
                            <Space>
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={() => fetchCards()}
                                >
                                    Refresh
                                </Button>
                                <Button
                                    icon={<FilterOutlined />}
                                    onClick={handleResetFilters}
                                >
                                    Reset Filters
                                </Button>
                            </Space>
                        </div>
                    </Col>
                </Row>

                <Row className="mt-3">
                    <Col span={24}>
                        <Space>
                            <Tag color="blue">
                                {filters.expiryFilter === 'active' && `Active Cards: ${pagination.totalActiveCards || 0}`}
                                {filters.expiryFilter === 'expired' && `Expired Cards: ${pagination.totalExpiredCards || 0}`}
                                {filters.expiryFilter === 'all' && `Total Cards: ${pagination.totalCards || 0}`}
                            </Tag>
                            <Tag color="purple">Page: {pagination.currentPage} / {pagination.totalPages}</Tag>
                        </Space>
                    </Col>
                </Row>
            </AntCard>

            <div className='flex justify-center overflow-x-auto'>
                <div className='w-full mt-5'>
                    <Table
                        columns={columns}
                        loading={loader}
                        dataSource={allCards}
                        pagination={{
                            current: pagination.currentPage,
                            pageSize: pagination.limit,
                            total: filters.expiryFilter === 'active'
                                ? (pagination.totalActiveCards || 0)
                                : filters.expiryFilter === 'expired'
                                    ? (pagination.totalExpiredCards || 0)
                                    : (pagination.totalCards || 0),
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} cards`,
                            pageSizeOptions: ['10', '20', '50', '100'],
                            onChange: (page, pageSize) => {
                                setFilters(prev => ({
                                    ...prev,
                                    page: page,
                                    limit: pageSize
                                }));
                            }
                        }}
                        rowKey="_id"
                    />
                </div>
            </div>

            <ToastContainer />
        </>
    )
}

export default AllCards